// The company's own day and clock (31 October 2026), one rule for the
// server, the web and the iPhone.
//
// A business's days are its own: an invoice is issued, a payment received
// and a visit booked on a day in the company's calendar, its reminder
// policy's zone, else London's (server/company-day.js). Every /sync/pull page
// and /auth/me name it as `timeZone`. Whoever reads a day or a time of the
// company's work reads it here, on that zone, never on the reader's own: a
// browser in Auckland is in tomorrow for half of every London day, and one in
// Los Angeles in yesterday for eight hours of it. The iPhone's twin is
// `CompanyClock` (ios/SendinvoCommon/CompanyClock.swift).
//
// What already happened, said to the reader (an Activity line's "Today at
// 9:41"), is on the reader's own clock, and is not read here.
//
// Pure: Intl only, no imports, so the browser bundle, the server and the
// iPhone's bundle can each take it.

export const DEFAULT_TIME_ZONE = "Europe/London";

/** The zone itself when the runtime knows it, else London. */
export function knownTimeZone(timeZone) {
  if (typeof timeZone !== "string" || !timeZone) return DEFAULT_TIME_ZONE;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone });
    return timeZone;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

function partsAt(ms, timeZone) {
  return Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit"
    }).formatToParts(new Date(ms)).map((p) => [p.type, p.value])
  );
}

function msOf(instant) {
  if (instant instanceof Date) return instant.getTime();
  if (typeof instant === "number") return instant;
  return Date.parse(instant ?? "");
}

/**
 * An instant on a zone's clock: `{ date: "YYYY-MM-DD", minute }`, minutes
 * since its midnight, or null for something that isn't an instant.
 */
export function clockInZone(instant, timeZone = DEFAULT_TIME_ZONE) {
  const ms = msOf(instant);
  if (!Number.isFinite(ms)) return null;
  const p = partsAt(ms, knownTimeZone(timeZone));
  return { date: `${p.year}-${p.month}-${p.day}`, minute: (Number(p.hour) % 24) * 60 + Number(p.minute) };
}

/** The day an instant falls on in a zone, "YYYY-MM-DD", or "" for none. */
export function dayInZone(instant, timeZone = DEFAULT_TIME_ZONE) {
  return clockInZone(instant, timeZone)?.date ?? "";
}

// The offset of a zone from UTC at the instant `ms`, in milliseconds.
function zoneOffset(ms, timeZone) {
  const p = partsAt(ms, timeZone);
  const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour) % 24, Number(p.minute), Number(p.second));
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/**
 * The instant a wall-clock time on a day in a zone is, in milliseconds:
 * "2026-10-25" at 1:30am in London is 00:30 UTC (still summer time, the
 * first of the two 1:30s), and a time the clocks skip in spring is the moment
 * after the jump. Minutes may be 1440, the midnight that ends the day.
 */
export function zonedInstant(day, minutes, timeZone = DEFAULT_TIME_ZONE) {
  const [y, m, d] = String(day).slice(0, 10).split("-").map(Number);
  const wall = Date.UTC(y, m - 1, d, 0, Math.round(Number(minutes) || 0));
  const zone = knownTimeZone(timeZone);
  // The offsets either side of the day: the same twice on all but the two
  // days a year the clocks change.
  const before = zoneOffset(wall - 12 * 3600000, zone);
  const after = zoneOffset(wall + 12 * 3600000, zone);
  const fits = [before, after]
    .map((offset) => wall - offset)
    .filter((instant) => wall - zoneOffset(instant, zone) === instant);
  // Autumn: two instants show this time, and the earlier is the one meant.
  if (fits.length) return Math.min(...fits);
  // Spring: none does, as the clocks jump past it. Read it on the clock from
  // before the jump, which lands just after it.
  return wall - before;
}

/** Whole days from one "YYYY-MM-DD" to another, on the calendar; NaN for a day that isn't one. */
export function daysFromTo(from, to) {
  const at = (day) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(day ?? ""));
    return match ? Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : NaN;
  };
  return Math.round((at(to) - at(from)) / 86400000);
}
